import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GradingOutlinedIcon from "@mui/icons-material/GradingOutlined";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { page, remove } from "./api-atividade";
import { setAtividades, setAtividade } from "./actions/actions";
import { setAlunoTurma } from "../AlunoTurma/actions/actions";
import TablePaginationActions from "../../shared/components/Table/Actions";
import { dateFormat, dateTimeFormat } from "../../util/DateUtil";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { success } from "../../util/ToastUtil";
import Loading from "../UI/Loading";

export default function AtividadeTable() {
  const dispatch = useDispatch();
  const tdp = useSelector((state) => state.tdp);
  const atividades = useSelector((state) => state.atividades);
  const codTdp = tdp.codigo;
  // Ao voltar do formulário, mantém a página em que estava (se for da mesma turma - disciplina).
  const [pageNumber, setPageNumber] = useState(() =>
    atividades?.content?.[0]?.tdp?.codigo === codTdp ? atividades.number ?? 0 : 0,
  );
  const [reloadKey, setReloadKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    page(codTdp, pageNumber)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          dispatch(setAtividades(data));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [codTdp, pageNumber, reloadKey, dispatch]);

  const handleDelete = (atividade) => {
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover a atividade "${atividade.descricao}"?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(atividade.codigo).then((data) => {
              if (data?.error) {
                errorMessage(data.message);
                return;
              }
              // Se era a única da página, volta uma página.
              if (atividades.content.length === 1 && pageNumber > 0) {
                setPageNumber((p) => p - 1);
              } else {
                setReloadKey((key) => key + 1);
              }
              success("Atividade removida.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  const content = atividades?.content ?? [];

  const renderTabela = () => {
    if (isLoading) {
      return (
        <Box py={6}>
          <Loading description="Carregando atividades..." />
        </Box>
      );
    }
    if (content.length === 0) {
      return (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            color: "text.secondary",
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <AssignmentOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1}>
            Nenhuma atividade cadastrada para esta turma - disciplina.
          </Typography>
        </Box>
      );
    }
    return (
      <>
        <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 640 }} aria-label="Atividades">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover" } }}>
                <TableCell sx={{ width: 110 }}>Data</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell align="right" sx={{ width: 220 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {content.map((atividade) => (
                <TableRow key={atividade.codigo} hover>
                  <TableCell>{atividade.data ? dateFormat(atividade.data) : "--"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {atividade.descricao}
                    </Typography>
                    {(atividade.semNota || atividade.entrega) && (
                      <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" useFlexGap>
                        {atividade.semNota && <Chip size="small" variant="outlined" label="Sem nota" />}
                        {atividade.entrega && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="secondary"
                            label={
                              atividade.dataLimiteEntrega
                                ? `Entrega até ${dateTimeFormat(atividade.dataLimiteEntrega)}`
                                : "Com entrega"
                            }
                          />
                        )}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<GradingOutlinedIcon />}
                        component={Link}
                        to="/atividade-aluno"
                        onClick={() => {
                          dispatch(setAtividade(atividade));
                          dispatch(setAlunoTurma(null));
                        }}
                      >
                        Alunos
                      </Button>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => dispatch(setAtividade(atividade))}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton size="small" onClick={() => handleDelete(atividade)}>
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          rowsPerPage={atividades.size || 0}
          component="div"
          count={atividades.totalElements || 0}
          page={atividades.number || 0}
          onPageChange={(event, newPage) => setPageNumber(newPage)}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? `${count} registros` : `mais de ${to}`}`
          }
          ActionsComponent={TablePaginationActions}
        />
      </>
    );
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          Atividades
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => dispatch(setAtividade({ tdp }))}>
          Nova Atividade
        </Button>
      </Stack>
      {renderTabela()}
    </Box>
  );
}
